import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { useHandleNetworkError } from "utils/useHandleNetworkError";

export const useGetChatGroups = () => {

  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: ["chatGroups", tenantVal],
    queryFn: async () => {
      const axiosInstance = createAxiosInstance();
      // /chat/groups/my returns the caller's group memberships
      // annotated with unreadCount + lastMessageAt so the sidebar can
      // surface badges and sort by recency without an extra roundtrip.
      const response = await axiosInstance.get(`${tenantVal}/chat/groups/my`);
      return response.data || [];
    },
    // Match the DM refetch cadence so channel unread badges surface
    // within ~10 seconds.
    refetchInterval: 10000,
    refetchOnWindowFocus: true,
    enabled: !!tenantVal,
    meta: { errorMessage: "Error fetching chat groups" },
  });
};

export const useCreateChatGroup = () => {
  const queryClient = useQueryClient();
  const handleNetworkError = useHandleNetworkError("Error creating chat group");

  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  return useMutation({
    mutationFn: async ({ name, description }) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(`${tenantVal}/chat/group`, {
        name,
        description,
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success("Channel created successfully");
      queryClient.invalidateQueries({ queryKey: ["chatGroups"] });
    },
    onError: handleNetworkError,
  });
};

export const useUpdateChatGroup = () => {
  const queryClient = useQueryClient();
  const handleNetworkError = useHandleNetworkError("Error updating chat group");

  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  return useMutation({
    mutationFn: async ({ groupId, name, description }) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.put(`${tenantVal}/chat/group/${groupId}`, {
        name,
        description,
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success("Channel updated successfully");
      queryClient.invalidateQueries({ queryKey: ["chatGroups"] });
    },
    onError: handleNetworkError,
  });
};

export const useDeleteChatGroup = () => {
  const queryClient = useQueryClient();
  const handleNetworkError = useHandleNetworkError("Error deleting chat group");

  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  return useMutation({
    mutationFn: async (groupId) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.delete(`${tenantVal}/chat/group/${groupId}`);
      return response.data;
    },
    onSuccess: () => {
      toast.success("Channel deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["chatGroups"] });
    },
    onError: handleNetworkError,
  });
};

export const useGetGroupMembers = (groupId) => {

  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: ["chatGroupMembers", tenantVal, groupId],
    queryFn: async () => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(`${tenantVal}/chat/group/${groupId}/members`);
      return response.data || [];
    },
    enabled: !!tenantVal && !!groupId,
    meta: { errorMessage: "Error fetching group members" },
    // Auth errors from this endpoint are an expected branch (non-member
    // opening a group via stale cache or shared link). The component
    // checks `error.response.status === 403` and renders an
    // access-denied state, so there is nothing to retry.
    retry: (failureCount, error) => {
      const status = error?.response?.status;
      if (status === 403 || status === 404) return false;

      return failureCount < 3;
    },
  });
};

export const useAddGroupMember = () => {
  const queryClient = useQueryClient();
  const handleNetworkError = useHandleNetworkError("Error adding member to group");

  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  return useMutation({
    mutationFn: async ({ groupId, staffMemberId, role }) => {
      const axiosInstance = createAxiosInstance();
      // Backend expects ChatGroupMember { staffMemberId, role? } —
      // older callers passed `staffId`, we now normalise here.
      const response = await axiosInstance.post(
        `${tenantVal}/chat/group/${groupId}/members`,
        role ? { staffMemberId, role } : { staffMemberId }
      );

      return response.data;
    },
    onSuccess: (_, { groupId }) => {
      toast.success("Member added to channel");
      queryClient.invalidateQueries({ queryKey: ["chatGroupMembers", tenantVal, groupId] });
    },
    // 403 / 404 / 409 are expected branches handled by the caller —
    // suppress the generic red toast for these and let the component
    // render inline messages or access-denied state.
    onError: (error, { groupId }) => {
      const status = error?.response?.status;
      if (status === 404) {
        queryClient.invalidateQueries({
          queryKey: ["chatGroupMembers", tenantVal, groupId],
        });
        return;
      }
      if (status === 403 || status === 409) return;
      handleNetworkError(error);
    },
  });
};

export const useRemoveGroupMember = () => {
  const queryClient = useQueryClient();
  const handleNetworkError = useHandleNetworkError(
    "Error removing member from group"
  );

  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  return useMutation({
    mutationFn: async ({ groupId, memberId }) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.delete(
        `${tenantVal}/chat/group/${groupId}/members/${memberId}`
      );

      return response.data;
    },
    onSuccess: (_, { groupId }) => {
      toast.success("Member removed from channel");
      queryClient.invalidateQueries({
        queryKey: ["chatGroupMembers", tenantVal, groupId],
      });
      // The caller may also have just removed themselves — refresh the
      // group list so the sidebar drops the channel from the user's
      // memberships if so.
      queryClient.invalidateQueries({ queryKey: ["chatGroups", tenantVal] });
    },
    onError: (error, { groupId }) => {
      const status = error?.response?.status;
      if (status === 404) {
        // Treat as already-removed — refresh and silently succeed.
        queryClient.invalidateQueries({
          queryKey: ["chatGroupMembers", tenantVal, groupId],
        });
        return;
      }
      if (status === 403) return;
      handleNetworkError(error);
    },
  });
};